// Hash router (createHashRouter) — chosen so the same build runs from a static
// host AND from the Capacitor native webview without server-side route config.
// Role-gated sections are declared HERE, on the route table itself: each gated
// group is a pathless parent carrying `handle: { role }` (RouteHandle), and
// RoleGuard (Layout.tsx) derives its decision from the matched route — so a new
// gated screen is gated by construction, never by a separate prefix list.
import { createHashRouter, Navigate } from 'react-router-dom'
import type { ComponentType } from 'react'
import Layout from './app/Layout'
import { ROUTES, type RouteHandle } from './app/nav'

type ScreenModule = { default: ComponentType }
/** React Router lazy module adapter. Layout stays eager so identity/role guards
 *  mount immediately; each screen becomes an independently cached chunk. */
const screen = (load: () => Promise<ScreenModule>) => async () => ({ Component: (await load()).default })

const login = screen(() => import('./screens/Login'))
// Prototype galleries and partner-integration sandboxes must not be addressable
// in a normal production build. Review builds opt in explicitly.
const showReviewSurfaces = import.meta.env.DEV || import.meta.env.VITE_SHOW_SCREEN_INDEX === 'true'

export const router = createHashRouter([
  {
    element: <Layout />,
    children: [
      { path: ROUTES.login, lazy: login },
      {
        // Customer workspace (APP-ACC-10): an exclusive approved installer must
        // never render a synthetic Home or owner controls from a direct deep link.
        handle: { workspace: 'homeowner' } satisfies RouteHandle,
        children: [
          { path: ROUTES.home, lazy: screen(() => import('./screens/Home')) },
          { path: ROUTES.homeDetails, lazy: screen(() => import('./screens/HomeDetails')) },
          { path: ROUTES.workModes, lazy: screen(() => import('./screens/WorkModes')) },
          { path: ROUTES.touSchedule, lazy: screen(() => import('./screens/TouSchedule')) },
          { path: ROUTES.touWindow, lazy: screen(() => import('./screens/TouWindow')) },
          { path: ROUTES.tariff, lazy: screen(() => import('./screens/Tariff')) },
          { path: ROUTES.tariffPeriod, lazy: screen(() => import('./screens/TouPeriod')) },
          { path: ROUTES.tariffTier, lazy: screen(() => import('./screens/TariffTier')) },
          { path: ROUTES.flatRate, lazy: screen(() => import('./screens/FlatRate')) },
          { path: ROUTES.tariffDemand, lazy: screen(() => import('./screens/DemandCharge')) },
          { path: ROUTES.homeEditor, lazy: screen(() => import('./screens/HomeEditor')) },
          { path: ROUTES.history, lazy: screen(() => import('./screens/EnergyHistory')) },
          { path: ROUTES.batteryPack, lazy: screen(() => import('./screens/BatteryPack')) },
          { path: ROUTES.inverter, lazy: screen(() => import('./screens/Inverter')) },
          { path: ROUTES.deviceLink, lazy: screen(() => import('./screens/DeviceLink')) },
          { path: ROUTES.consent, lazy: screen(() => import('./screens/ConsentCentre')) },
          { path: ROUTES.admTransparency, lazy: screen(() => import('./screens/AdmTransparency')) },
          { path: ROUTES.devices, lazy: screen(() => import('./screens/Devices')) },
          { path: ROUTES.ownerAdd, lazy: screen(() => import('./screens/OwnerAdd')) },
          { path: ROUTES.ownership, lazy: screen(() => import('./screens/Ownership')) },
          { path: ROUTES.batterySettings, lazy: screen(() => import('./screens/BatterySettings')) },
          { path: ROUTES.manualOperations, lazy: screen(() => import('./screens/ManualOperations')) },
          { path: ROUTES.wifiReconnect, lazy: screen(() => import('./screens/WifiReconnect')) },
          { path: ROUTES.softwareUpdate, lazy: screen(() => import('./screens/SoftwareUpdate')) },
          { path: ROUTES.supportMessages, lazy: screen(() => import('./screens/SupportMessages')) },
        ],
      },
      { path: ROUTES.notifications, lazy: screen(() => import('./screens/Notifications')) },
      { path: ROUTES.menu, lazy: screen(() => import('./screens/MenuHub')) },
      { path: ROUTES.security, lazy: screen(() => import('./screens/Security')) },
      // Public FAQ (APP-ACC-06) — no workspace gate so guests can read without sign-in.
      { path: ROUTES.helpFaq, lazy: screen(() => import('./screens/HelpFaq')) },
      {
        // Installer workspace (APP-ACC-09): every screen in this group needs the
        // server-granted installer role — add new installer screens HERE.
        handle: { role: 'installer' } satisfies RouteHandle,
        children: [
          { path: ROUTES.installerAdd, lazy: screen(() => import('./screens/InstallerAdd')) },
          { path: ROUTES.installerResidence, lazy: screen(() => import('./screens/Residence')) },
          { path: ROUTES.commissioning, lazy: screen(() => import('./screens/Commissioning')) },
          { path: ROUTES.diagnostics, lazy: screen(() => import('./screens/Diagnostics')) },
          { path: ROUTES.compliance, lazy: screen(() => import('./screens/Compliance')) },
          { path: ROUTES.firmware, lazy: screen(() => import('./screens/Firmware')) },
          { path: ROUTES.fleet, lazy: screen(() => import('./screens/InstallerFleet')) },
          { path: ROUTES.installerOrganisation, lazy: screen(() => import('./screens/InstallerOrganisation')) },
          { path: ROUTES.installerInventory, lazy: screen(() => import('./screens/InstallerInventory')) },
          // Retired approval-handover URLs must not expose the legacy workflow.
          { path: ROUTES.installerRequests, element: <Navigate replace to={ROUTES.fleet} /> },
        ],
      },
      {
        // Back-office console (CLD-PRT-09 / SYS-SW-07).
        handle: { role: 'admin' } satisfies RouteHandle,
        children: [
          { path: ROUTES.adminPortal, lazy: screen(() => import('./screens/AdminPortal')) },
          { path: ROUTES.admin, lazy: screen(() => import('./screens/AdminRequests')) },
        ],
      },
      {
        // The Support Portal has a distinct identity and route authority from Admin.
        handle: { role: 'support' } satisfies RouteHandle,
        children: [{ path: ROUTES.supportConsole, lazy: screen(() => import('./screens/SupportConsole')) }],
      },
      { path: ROUTES.documents, lazy: screen(() => import('./screens/Documents')) },
      ...(showReviewSurfaces ? [
        { path: ROUTES.weatherStates, lazy: screen(() => import('./screens/WeatherStates')) },
        // Partner sandbox uses published demo credentials by design. It belongs
        // only in an explicit integration-review build, never the homeowner app.
        { path: ROUTES.vppPartner, lazy: screen(() => import('./screens/VppPartner')) },
        { path: ROUTES.screens, lazy: screen(() => import('./screens/ScreenIndex')) },
      ] : []),
      { path: '*', lazy: screen(() => import('./screens/WorkspaceIndex')) },
    ],
  },
])
